import type {Component, Editor, SelectComponentOptions} from 'grapesjs';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';
import {MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import type {PosterPageData} from '@Libraries/poster-library';
import {getPostersPageIds} from '@Libraries/poster-library';
import type {EmbeddedEditorOpenDesignConfig} from '@Libraries/embedded-editor';
import {EmbeddedEditorAction, EmbeddedEditorElementsToHide, loadEmbeddedEditorPlugin} from '@Libraries/embedded-editor';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {UpsertComponentMethod} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {openDesignSelector, openDesignSelectorLegacy} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/design-selector-helper';
import {finishLoading} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/editor-helper';
import {getBooleanProp, getMainWrapper, getNumberProp, getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {removeComponentAnchor} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {canCurrentUserAutosaveTemplate, saveEmailTemplateToCache} from '@EmailMakerReact/lib/email-maker-storage-library';
import {setImageSrc} from '@EmailMakerReact/lib/email-maker-media-library';
import {loadDesignForId, loadDesignPageForId} from '@EmailMakerReact/lib/email-maker-design-preview-library';
import {authenticateUser} from '@EmailMakerReact/lib/email-maker-publish-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

/**
 * PMW design component insertion, selector, reload, and embedded poster-editor flows for the React email maker.
 */

/** avoidStore/noCount are PMW extensions forwarded to GrapesJS listeners. */
const SELECT_WITHOUT_STORE_OPTS: SelectComponentOptions & {avoidStore: boolean; noCount: boolean} = {
  avoidStore: true,
  noCount: true,
};

const EMBEDDED_POSTERMAKER_IFRAME_ID = 'posterMyWallEditor';
const EMBEDDED_EDITOR_INIT_UI_MESSAGE = 'INIT_UI';

export interface InsertComponentOptions {
  /** Falls back to the current selection (or the content container) when omitted; required for REPLACE_WITH. */
  componentToAddTo?: Component;
  /** The component type to insert. */
  componentToAdd: string;
  upsertionMethod: UpsertComponentMethod;
}

/** Inserts/replaces a component, walking up parents until GrapesJS allows the move. */
export function insertComponent(editor: Editor, options: InsertComponentOptions): Component | undefined {
  const {componentToAddTo, upsertionMethod} = resolveComponentToAddTo(editor, options.componentToAddTo, options.upsertionMethod);
  if (!componentToAddTo) {
    logEmailMakerError('insertComponent: unable to get component to insert/append to', undefined);
    return undefined;
  }

  if (upsertionMethod === UpsertComponentMethod.REPLACE_WITH) {
    return componentToAddTo.replaceWith({type: options.componentToAdd})[0] ?? undefined;
  }

  const componentToInsertIn = getComponentToInsertInto(editor, componentToAddTo, options.componentToAdd, upsertionMethod);
  if (!componentToInsertIn) {
    logEmailMakerError('insertComponent: unable to get component parent for appendment/insertion', undefined);
    return undefined;
  }

  const indexToInsertIn = getInsertionIndex(componentToAddTo, componentToInsertIn, upsertionMethod);

  return componentToInsertIn.components().add({type: options.componentToAdd}, {at: indexToInsertIn});
}

export function replaceEmptyTextWithDivider(editor: Editor, component: Component): void {
  const isLocked = !getBooleanProp(component, 'draggable');
  removeComponentAnchor(editor, component);

  const dividerComponent = insertComponent(editor, {
    componentToAddTo: component,
    componentToAdd: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER,
    upsertionMethod: UpsertComponentMethod.REPLACE_WITH,
  });

  if (!dividerComponent) {
    return;
  }

  dividerComponent.set('draggable', !isLocked);
  editor.select(dividerComponent);
}

/** Locks down the root wrapper and ensures the PMW content scaffolding exists (load listener). */
export function addContentComponent(editor: Editor): void {
  const children = editor.getComponents();

  editor.getWrapper()?.set({
    droppable: false,
    stylable: false,
    draggable: false,
    highlightable: false,
    selectable: false,
  });

  if (children.models.length === 0) {
    placeMainContent(editor);
    return;
  }

  backwardsCompatibilityForDivs(editor);
  const mainWrapper = getMainWrapper(editor);
  editor.select(mainWrapper, SELECT_WITHOUT_STORE_OPTS);
}

/** Adds the PMW wrapper scaffolding for a blank template, keeping it out of the undo history. */
function placeMainContent(editor: Editor): void {
  const mainWrapper = editor.addComponents({
    type: EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER,
  })[0];

  const mainComponent = mainWrapper.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT}"]`)[0];

  editor.select(mainWrapper);

  editor.UndoManager.remove(mainWrapper);
  editor.UndoManager.remove(mainComponent);
  editor.UndoManager.clear();
}

/** Rebuilds pre-content-container templates around the current PMW wrapper structure, then persists for autosave-capable users. */
function backwardsCompatibilityForDivs(editor: Editor): void {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper) {
    return;
  }

  const mainComponent = wrapper.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT}"]`)[0];
  const oldWrapper = getMainWrapper(editor);
  const newWrapperContainer = wrapper.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER}"]`);

  if (!mainComponent || !oldWrapper || newWrapperContainer.length !== 0) {
    return;
  }

  const components = mainComponent.components();

  oldWrapper.replaceWith({
    type: EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER,
  });

  const newWrapper = wrapper.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER}"]`)[0];

  if (!newWrapper) {
    return;
  }

  components.each((child) => {
    newWrapper.components().add(child);
  });

  if (canCurrentUserAutosaveTemplate(editor)) {
    void saveEmailTemplateToCache(editor);
  }
}

/** Resolves the insertion target from the selection / content container. */
function resolveComponentToAddTo(
  editor: Editor,
  componentToAddTo: Component | undefined,
  upsertionMethod: UpsertComponentMethod
): {componentToAddTo: Component | undefined; upsertionMethod: UpsertComponentMethod} {
  if (componentToAddTo) {
    return {componentToAddTo, upsertionMethod};
  }

  if (upsertionMethod === UpsertComponentMethod.REPLACE_WITH) {
    logEmailMakerError('insertComponent: REPLACE_WITH requires componentToAddTo to be passed', undefined);
    return {componentToAddTo: undefined, upsertionMethod};
  }

  const editorSelected = editor.getSelected();
  const isNoComponentSelected = editorSelected ? editorSelected.is(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER) : false;
  const editorContentContainer = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER)[0];

  if (isNoComponentSelected || !editorSelected) {
    return {componentToAddTo: editorContentContainer, upsertionMethod: UpsertComponentMethod.INSERT_INTO};
  }

  return {componentToAddTo: editorSelected, upsertionMethod};
}

/** Walks up from the target until GrapesJS allows the move. */
function getComponentToInsertInto(editor: Editor, componentToAddTo: Component, componentToAdd: string, upsertionMethod: UpsertComponentMethod): Component | undefined {
  let componentToInsertIn: Component | undefined =
    upsertionMethod === UpsertComponentMethod.APPEND_AFTER || upsertionMethod === UpsertComponentMethod.PREPEND_BEFORE ? componentToAddTo.parent() : componentToAddTo;

  while (componentToInsertIn) {
    if (editor.Components.canMove(componentToInsertIn, componentToAdd).result) {
      return componentToInsertIn;
    }

    componentToInsertIn = componentToInsertIn.parent();
  }

  return undefined;
}

/** Returns the index at which to insert the component given the upsert method. */
function getInsertionIndex(componentToAddTo: Component, componentToInsertIn: Component, upsertionMethod: UpsertComponentMethod): number {
  if (upsertionMethod === UpsertComponentMethod.APPEND_AFTER) {
    return (componentToAddTo.index() ?? -1) + 1;
  }

  if (upsertionMethod === UpsertComponentMethod.PREPEND_BEFORE) {
    return (componentToAddTo.index() ?? 1) - 1;
  }

  const lastChild = componentToInsertIn.getLastChild();
  return (lastChild?.index() ?? -1) + 1;
}

export interface OpenDesignSelectorAndInsertOptions {
  /** Explicit insertion target; falls back to the selection / content container when omitted. */
  model?: Component;
  /** Replace the target instead of appending after it. */
  replaceComponent?: boolean;
  /** Default: true. */
  isMultiSelectAllowed?: boolean;
}

export interface OpenPostermakerForDesignOptions {
  /** Design to open; falls back to the selected component's design id. */
  id?: string;
}

/** Whether the design component's creator is the current user. */
export function doesPmwDesignBelongToUser(component: Component | undefined): boolean {
  const currentUserId = getUserId();
  if (!currentUserId || !component) {
    return false;
  }

  const designCreatorId = getNumberProp(component, EMAIL_MAKER_PROPS.DESIGN_CREATOR_ID);
  if (!designCreatorId) {
    return false;
  }

  return designCreatorId === currentUserId;
}

/** Unique ids of user-owned premium (getty) designs in the email. */
export function getPremiumDesignIds(editor: Editor): string[] {
  const myDesigns = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN);
  if (!myDesigns) {
    return [];
  }

  const premiumDesignIds = myDesigns
    .filter((design) => {
      return Boolean(design.get(EMAIL_MAKER_PROPS.IS_PREMIUM_DESIGN)) && doesPmwDesignBelongToUser(design);
    })
    .map((design) => {
      return getStringProp(design, EMAIL_MAKER_PROPS.DESIGN_ID) ?? '';
    });

  return [...new Set(premiumDesignIds)];
}

/** Backfills the design component's page id from the poster's pages. */
export async function populateMyDesignPageId(editor: Editor, component?: Component): Promise<void> {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  const posterId = getStringProp(target, EMAIL_MAKER_PROPS.DESIGN_ID);
  if (!posterId) {
    return;
  }

  const pageIdsMap = await getPostersPageIds([posterId]);
  const pageId = pageIdsMap[posterId]?.[0];
  if (!pageId) {
    return;
  }

  target.set(EMAIL_MAKER_PROPS.DESIGN_PAGE_ID, pageId);
}

/** Opens the my-designs selector (behind the auth gate) and inserts a design component per picked page. */
export function openDesignSelectorAndInsert(editor: Editor, options: OpenDesignSelectorAndInsertOptions = {}): void {
  const selected = editor.getSelected();
  const upsertionMethod = options.replaceComponent ? UpsertComponentMethod.REPLACE_WITH : UpsertComponentMethod.APPEND_AFTER;

  const openSelector = (): void => {
    if (window.PMW.enableMyStuffMultipagesUI) {
      openDesignSelector(
        (pagesData: PosterPageData[]): void => {
          let componentToAddTo = resolveDesignInsertionTarget(editor, selected, options.model);

          pagesData.forEach((pageData: PosterPageData) => {
            const insertedComponent = insertDesignComponent(editor, componentToAddTo, upsertionMethod);
            if (!insertedComponent) {
              logEmailMakerError('openDesignSelectorAndInsert: failed to insert design component', undefined);
              return;
            }

            componentToAddTo = insertedComponent;
            void loadDesignPageForId(editor, insertedComponent, {posterHashedId: pageData.posterId, pageHashedId: pageData.pageId});
          });
        },
        {beforeLoad: finishLoading, isMultiselectAllowed: options.isMultiSelectAllowed ?? true}
      );
      return;
    }

    openDesignSelectorLegacy((id: string): void => {
      const componentToAddTo = resolveDesignInsertionTarget(editor, selected, options.model);
      const insertedComponent = insertDesignComponent(editor, componentToAddTo, upsertionMethod);
      if (!insertedComponent) {
        console.error('Error while inserting component');
        return;
      }

      void loadDesignForId(editor, insertedComponent, id);
    }, finishLoading);
  };

  if (!isUserLoggedIn()) {
    authenticateUser(editor, {cb: openSelector});
    return;
  }

  openSelector();
}

/** Re-fetches the preview for a design component flagged as modified. */
export function reloadModifiedDesign(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  const isModified = Boolean(component.get(EMAIL_MAKER_PROPS.DESIGN_MODIFIED));
  const designId = getStringProp(component, EMAIL_MAKER_PROPS.DESIGN_ID);
  if (!isModified || !designId) {
    return;
  }

  if (window.PMW.enableMyStuffMultipagesUI) {
    const pageId = getStringProp(component, EMAIL_MAKER_PROPS.DESIGN_PAGE_ID);
    if (pageId) {
      void loadDesignPageForId(editor, component, {posterHashedId: designId, pageHashedId: pageId});
    }
    return;
  }

  void loadDesignForId(editor, component, designId);
}

/** Flags every design component as modified and reloads it (ON_ALL_COMPONENTS_LOADED path). */
export function reloadAllDesigns(editor: Editor): void {
  const myDesigns = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN);
  myDesigns?.forEach((myDesign) => {
    myDesign.set(EMAIL_MAKER_PROPS.DESIGN_MODIFIED, true);
    reloadModifiedDesign(editor, myDesign);
  });
}

/** Re-resolves getty background URLs (e.g. after the user's premium status changes). */
export function reloadPremiumBackgrounds(editor: Editor): void {
  editor.getWrapper()?.onAll((component) => {
    const source = getStringProp(component, EMAIL_MAKER_PROPS.CONTENT_SOURCE);
    const style = component.get('style') as Record<string, unknown> | undefined;
    const backgroundImage = style?.['background-image'] ?? '';
    const hasPremiumContent = source === MULTI_MEDIA_SOURCE_GETTY;

    if (!hasPremiumContent || !backgroundImage) {
      return;
    }

    void setImageSrc(editor, {
      component,
      source,
      isBackground: true,
      hashedFilename: getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '',
    });
  });
}

/** Opens the embedded poster editor for the design (always as a copy), loading the editor plugin on first use, and reloads the preview after save. */
export function openPostermakerForDesign(editor: Editor, options: OpenPostermakerForDesignOptions = {}): void {
  if (!isUserLoggedIn()) {
    authenticateUser(editor, {
      cb: (): void => {
        openPostermakerForDesign(editor, options);
      },
    });
    return;
  }

  loadEmbeddedEditorPlugin((): void => {
    openDesignInLoadedPostermaker(editor, options);
  });
}

function openDesignInLoadedPostermaker(editor: Editor, options: OpenPostermakerForDesignOptions): void {
  const selected = editor.getSelected();
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const designId = options.id || (selected ? getStringProp(selected, EMAIL_MAKER_PROPS.DESIGN_ID) : undefined) || '';
  if (!designId) {
    return;
  }

  attachEmbeddedTitleListener();

  const editorOpts: EmbeddedEditorOpenDesignConfig = {
    elementsToHide: [EmbeddedEditorElementsToHide.FILE_DROPDOWN, EmbeddedEditorElementsToHide.DOWNLOAD_BUTTON],
    onSave: (savedId: string): void => {
      void onPostermakerDesignSaved(editor, selected, savedId);
    },
  };

  window.PMW.plugin.editor.openDesign(designId, EmbeddedEditorAction.COPY, editorOpts);
}

/** onSave callback for the embedded-editor opts: updates design props and reloads the preview. */
async function onPostermakerDesignSaved(editor: Editor, selected: Component | undefined, savedId: string): Promise<void> {
  selected?.set(EMAIL_MAKER_PROPS.DESIGN_ID, savedId);
  await populateMyDesignPageId(editor, selected);
  selected?.set(EMAIL_MAKER_PROPS.DESIGN_MODIFIED, true);
  window.PMW.plugin.editor.close();

  if (!savedId) {
    return;
  }

  // The editor always opens with the copy action, so the saved poster is marked internal.
  selected?.set(EMAIL_MAKER_PROPS.IS_INTERNAL, true);
  await setPosterInternal(savedId, true);
  reloadModifiedDesign(editor, selected);
}

/** Resolves the insertion target for a design component given the selection state. */
function resolveDesignInsertionTarget(editor: Editor, selected: Component | undefined, model: Component | undefined): Component | undefined {
  if (selected === undefined || model !== undefined) {
    return model;
  }

  const isEmailContainerSelected = editor.getSelected()?.is(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER);
  if (isEmailContainerSelected) {
    const contentContainer = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER)[0];
    return contentContainer?.getLastChild() ?? undefined;
  }

  return selected;
}

function insertDesignComponent(editor: Editor, componentToAddTo: Component | undefined, upsertionMethod: UpsertComponentMethod): Component | undefined {
  return insertComponent(editor, {
    componentToAddTo,
    componentToAdd: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN,
    upsertionMethod,
  });
}

async function setPosterInternal(hashedID: string, isInternal: boolean): Promise<void> {
  try {
    await window.PMW.writeLocal('poster/setPosterInternal', {hashedID, isInternal});
  } catch (e) {
    logEmailMakerError('setPosterInternal: failed', e);
  }
}

/** Registers the INIT_UI message listener that injects the email-maker title into the embedded editor's navbar. */
function attachEmbeddedTitleListener(): void {
  const onEmbeddedEditorMessage = (e: MessageEvent): void => {
    const root = `${window.PMW.BASE_URL.split('.com')[0]}.com`;
    if (e.origin !== root) {
      return;
    }

    if (addEmbeddedTitle(e)) {
      window.removeEventListener('message', onEmbeddedEditorMessage);
    }
  };

  window.addEventListener('message', onEmbeddedEditorMessage);
}

/** Injects the email-maker title into the embedded editor navbar on INIT_UI; returns true once injected so the listener can detach. */
function addEmbeddedTitle(e: MessageEvent): boolean {
  if (e.data !== EMBEDDED_EDITOR_INIT_UI_MESSAGE) {
    return false;
  }

  const iframe = document.getElementById(EMBEDDED_POSTERMAKER_IFRAME_ID);
  if (!(iframe instanceof HTMLIFrameElement)) {
    return false;
  }

  const innerDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!innerDoc) {
    return false;
  }

  const navbar = innerDoc.getElementById('nav');
  if (!navbar) {
    return false;
  }

  const logoBtn = navbar.querySelector('.logo.btn-go-home');
  const titleContainer = `<ul class="email-editor-title-container">
                    <span class="email-editor-title visible-desktop body-lg-bold">${window.i18next.t('pmwjs_edit_design')}</span>
            </ul>`;

  if (logoBtn) {
    logoBtn.remove();
    navbar.insertAdjacentHTML('afterbegin', titleContainer);
  }

  return true;
}
